import { useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Check, X, Plus, Download, ExternalLink, Sparkles, ListChecks } from "lucide-react";

export const CONSULENZA_ITEMS = [
  { key: "art119ter_c3", label: "Fornisce consulenza ai sensi dell'articolo 119-ter, comma 3, del Codice, ovvero una raccomandazione personalizzata." },
  { key: "art119ter_c4", label: "Fornisce consulenza fondata su un'analisi imparziale e personale ai sensi dell'art. 119-ter comma 4 del Codice delle Assicurazioni Private, in quanto fondata sull'analisi di un numero sufficiente di prodotti assicurativi disponibili sul mercato che gli consente di formulare una raccomandazione personalizzata secondo criteri professionali in merito al prodotto adeguato a soddisfare le esigenze del cliente." },
];

export const RICEVUTA_DECLS = [
  { key: "allegato3", label: "di aver preso visione/ricevuto l'Allegato 3 Modulo unico precontrattuale (MUP) per i prodotti assicurativi non d'investimento" },
  { key: "questionario", label: "di aver ricevuto il questionario delle richieste ed esigenze e la valutazione della coerenza del contratto offerto" },
  { key: "proposta", label: "di avere ricevuto la proposta assicurativa nonché tutta la documentazione precontrattuale" },
  { key: "privacy", label: "di aver ricevuto l'informativa sulla privacy" },
  { key: "trasmissione_elettronica", label: "di accettare la trasmissione di tutta la documentazione pre-contrattuale e contrattuale in formato elettronico, consapevole di poter modificare la preferenza e poter richiedere la documentazione su supporto cartaceo o altro supporto durevole" },
  { key: "testo_contrattuale", label: "di aver ricevuto il testo contrattuale della polizza/appendice" },
  { key: "conserva_copia", label: "di autorizzare l'intermediario a conservare, presso le sue strutture, copia della documentazione contrattuale firmata" },
];

const PROD_COLS = [
  { key: "compagnia", label: "Compagnia", type: "text" },
  { key: "collaborazione_orizzontale", label: "Collaborazione orizzontale", type: "yn" },
  { key: "nome_prodotto", label: "Nome prodotto", type: "text" },
  { key: "vendita_esclusiva", label: "Vendita in esclusiva?", type: "yn" },
  { key: "onorario_diretto", label: "Onorario corrisposto direttamente dal cliente?", type: "yn" },
  { key: "metodo_calcolo_onorario", label: "Metodo di calcolo dell'onorario", type: "text" },
  { key: "commissione_inclusa", label: "Commissione inclusa nel premio assicurativo?", type: "yn" },
  { key: "altro_compenso", label: "Altro tipo di compenso?", type: "text" },
  { key: "importo_valutazione_periodica", label: "Importo percepito per la valutazione periodica dell'adeguatezza", type: "text" },
];

const emptyProdRow = (compagniaName = "", nome = "", product_id = "") => ({
  product_id, compagnia: compagniaName, collaborazione_orizzontale: "no", nome_prodotto: nome,
  vendita_esclusiva: "no", onorario_diretto: "no", metodo_calcolo_onorario: "",
  commissione_inclusa: "si", altro_compenso: "no", importo_valutazione_periodica: "", selected: true,
});

const YnCell = ({ value, onChange, testid }) => (
  <Select value={value || "no"} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="h-8 w-16 mx-auto text-xs"><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="si">Sì</SelectItem>
      <SelectItem value="no">No</SelectItem>
    </SelectContent>
  </Select>
);

export default function MupValutazioneProdotto({ form, setForm, areas, products, companies, reloadCatalog }) {
  const [newCompany, setNewCompany] = useState("");
  const [newProdName, setNewProdName] = useState("");
  const [newProdCompany, setNewProdCompany] = useState("");
  const [newVoce, setNewVoce] = useState("");
  const [showBulk, setShowBulk] = useState(false);
  const [bulkSel, setBulkSel] = useState({});
  const [bulkQuery, setBulkQuery] = useState("");

  const area = areas.find((a) => a.area_id === form.area_id);
  const needs = form.needs_grid || [];

  const setNeed = (i, k, v) => setForm((f) => {
    const g = [...(f.needs_grid || [])];
    g[i] = { ...g[i], [k]: v };
    return { ...f, needs_grid: g };
  });

  const toggleNeed = (i, k) => setNeed(i, k, !needs[i]?.[k]);

  const allSelected = needs.length > 0 && needs.every((r) => r.richieste && r.proposte && r.confermate);
  const selectAll = (val) => setForm((f) => ({
    ...f, needs_grid: (f.needs_grid || []).map((r) => ({ ...r, richieste: val, proposte: val, confermate: val })),
  }));

  const addVoce = () => {
    const v = newVoce.trim();
    if (!v) return;
    setForm((f) => ({ ...f, needs_grid: [...(f.needs_grid || []), { voce: v, richieste: false, proposte: false, confermate: false }] }));
    setNewVoce("");
  };

  const removeNeed = (i) => setForm((f) => ({ ...f, needs_grid: (f.needs_grid || []).filter((_, idx) => idx !== i) }));

  // --- Elenco garanzie predefinite (dalle voci configurate nel Catalogo → Aree di tutela) ---
  const catalogVoci = useMemo(() => {
    const seen = new Map();
    (areas || []).forEach((a) => (a.voci || []).forEach((v) => {
      const key = String(v).trim();
      if (key && !seen.has(key)) seen.set(key, a.titolo || "");
    }));
    return [...seen.entries()].map(([voce, areaTitolo]) => ({ voce, areaTitolo }));
  }, [areas]);
  const currentVoci = useMemo(() => new Set((needs || []).map((r) => r.voce)), [needs]);
  const bulkAvailable = catalogVoci.filter((c) => !currentVoci.has(c.voce)
    && (!bulkQuery.trim() || c.voce.toLowerCase().includes(bulkQuery.trim().toLowerCase())));
  const bulkCount = Object.keys(bulkSel).filter((v) => bulkSel[v]).length;
  const addBulkVoci = () => {
    const toAdd = Object.keys(bulkSel).filter((v) => bulkSel[v] && !currentVoci.has(v));
    if (!toAdd.length) return;
    setForm((f) => ({ ...f, needs_grid: [...(f.needs_grid || []), ...toAdd.map((voce) => ({ voce, richieste: false, proposte: false, confermate: false }))] }));
    setBulkSel({}); setBulkQuery(""); setShowBulk(false);
    toast.success(`${toAdd.length} garanzie aggiunte`);
  };

  // --- Valutazione del prodotto (tabella di coerenza, voci dal prodotto, 3 stati Sì/No/N.A.) ---
  const grid = form.grid || [];
  const setGridCell = (i, k, v) => setForm((f) => {
    const g = [...(f.grid || [])];
    g[i] = { ...g[i], [k]: v };
    return { ...f, grid: g };
  });
  const gridAllSi = grid.length > 0 && grid.every((r) => r.richieste === "si" && r.proposte === "si" && r.confermate === "si");
  const selectAllGrid = () => setForm((f) => ({
    ...f, grid: (f.grid || []).map((r) => ({ ...r, richieste: "si", proposte: "si", confermate: "si" })),
  }));

  const prodRows = form.prodotto_rows || [];
  const setProd = (i, k, v) => setForm((f) => {
    const rows = [...(f.prodotto_rows || [])];
    rows[i] = { ...rows[i], [k]: v };
    return { ...f, prodotto_rows: rows };
  });
  const removeProd = (i) => setForm((f) => ({ ...f, prodotto_rows: (f.prodotto_rows || []).filter((_, idx) => idx !== i) }));

  const companyName = (cid) => companies.find((c) => c.company_id === cid)?.name || "";

  const addProductRow = (pid) => {
    const prod = products.find((p) => p.product_id === pid);
    if (!prod) return;
    if ((form.prodotto_rows || []).some((r) => r.product_id === pid)) {
      toast.info("Prodotto già presente in tabella");
      return;
    }
    setForm((f) => ({ ...f, prodotto_rows: [...(f.prodotto_rows || []), emptyProdRow(companyName(prod.company_id), prod.name, pid)] }));
  };

  const addBlankRow = () => setForm((f) => ({ ...f, prodotto_rows: [...(f.prodotto_rows || []), emptyProdRow()] }));

  const quickAddCompany = async () => {
    const name = newCompany.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/mup/companies/quick", { name });
      toast.success(`Compagnia "${data.name}" aggiunta al catalogo`);
      setNewCompany("");
      reloadCatalog?.();
    } catch (e) { toast.error("Errore aggiunta compagnia"); }
  };

  const quickAddProduct = async () => {
    const name = newProdName.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/mup/products/quick", { name, company_id: newProdCompany, area_id: form.area_id || "" });
      toast.success(`Prodotto "${data.name}" aggiunto al catalogo`);
      setForm((f) => ({ ...f, prodotto_rows: [...(f.prodotto_rows || []), emptyProdRow(companyName(data.company_id), data.name, data.product_id)] }));
      setNewProdName(""); setNewProdCompany("");
      reloadCatalog?.();
    } catch (e) { toast.error("Errore aggiunta prodotto"); }
  };

  const cons = form.consulenza || {};
  const setCons = (k, v) => setForm((f) => ({ ...f, consulenza: { ...(f.consulenza || {}), [k]: v } }));

  const [showAllProducts, setShowAllProducts] = useState(false);
  const selectableProducts = useMemo(() => {
    const active = products.filter((p) => p.active !== false);
    if (showAllProducts || !form.area_id) return active;
    const ofArea = active.filter((p) => (p.area_ids || []).includes(form.area_id));
    return ofArea.length > 0 ? ofArea : active;
  }, [products, showAllProducts, form.area_id]);

  return (
    <div className="space-y-8">
      {/* Placeholder integrations */}
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" data-testid="mup-assieasy-btn"
          onClick={() => toast.info("Integrazione AssiEasy in arrivo (segnaposto)")}>
          <Download className="h-4 w-4 mr-1.5" /> Ricevi dati da AssiEasy
        </Button>
        <Button type="button" variant="outline" size="sm" data-testid="mup-approfondisci-btn"
          onClick={() => toast.info("Approfondimento cliente in arrivo (segnaposto)")}>
          <Sparkles className="h-4 w-4 mr-1.5" /> Approfondisci cliente
        </Button>
        <Button type="button" variant="outline" size="sm" data-testid="mup-preventivass-btn"
          onClick={() => window.open("https://www.preventivass.it/", "_blank", "noopener,noreferrer")}>
          <ExternalLink className="h-4 w-4 mr-1.5" /> Preventivass
        </Button>
      </div>

      {/* Area di tutela */}
      <div className="max-w-md space-y-1.5">
        <Label className="text-slate-600 text-sm">Area di tutela *</Label>
        <Select value={form.area_id} onValueChange={pickArea}>
          <SelectTrigger data-testid="mup-area-select"><SelectValue placeholder="Seleziona l'area di tutela" /></SelectTrigger>
          <SelectContent>
            {areas.map((a) => (
              <SelectItem key={a.area_id} value={a.area_id}>{a.titolo} · {a.area_tutela}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {form.area_id && (
        <>
          {/* Valutazione del prodotto — Tabella di coerenza (voci generate dal prodotto) */}
          <div className="space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Valutazione del prodotto</h3>
                <p className="text-xs text-slate-500">Voci generate dal prodotto selezionato. Nessun campo è pre-selezionato: compila Sì / No / Non applicabile (conformità IVASS).</p>
              </div>
              <Button type="button" variant="outline" size="sm" data-testid="mup-grid-select-all" onClick={selectAllGrid} disabled={grid.length === 0}>
                <Check className="h-4 w-4 mr-1" /> Seleziona tutto (Sì)
              </Button>
            </div>
            {grid.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-300 p-4 text-sm text-slate-500" data-testid="mup-grid-empty">
                Seleziona un prodotto (passo «Compagnia e prodotto») per generare le voci della valutazione.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="text-left p-2 font-medium">Voce</th>
                      <th className="p-2 font-medium">Richieste ed esigenze del cliente</th>
                      <th className="p-2 font-medium">Proposte dell'intermediario</th>
                      <th className="p-2 font-medium">Confermate dal cliente</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {grid.map((r, i) => (
                      <tr key={i} data-testid={`mup-grid-row-${i}`}>
                        <td className="p-2 text-slate-900">{r.voce}</td>
                        {["richieste", "proposte", "confermate"].map((col) => (
                          <td key={col} className="p-2 text-center">
                            <Select value={r[col] && r[col] !== "non_compilato" ? r[col] : ""} onValueChange={(v) => setGridCell(i, col, v)}>
                              <SelectTrigger data-testid={`mup-grid-${i}-${col}`} className="h-8 w-[130px] mx-auto"><SelectValue placeholder="—" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="si">Sì</SelectItem>
                                <SelectItem value="no">No</SelectItem>
                                <SelectItem value="na">Non applicabile</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Aree di tutela (facoltativo) */}
          <div className="space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="text-lg font-semibold text-slate-900">Aree di tutela (facoltativo)</h3>
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer" data-testid="mup-select-all">
                <Switch checked={allSelected} onCheckedChange={selectAll} /> Seleziona tutto
              </label>
            </div>

            {needs.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-300 p-4 text-sm text-slate-500">
                Nessuna voce configurata per «{area?.titolo}». Aggiungi le voci qui sotto oppure configurale dal Catalogo.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="text-left p-2 font-medium">Voce</th>
                      <th className="p-2 font-medium">Richieste ed esigenze del cliente</th>
                      <th className="p-2 font-medium">Proposte dell'intermediario</th>
                      <th className="p-2 font-medium">Confermate dal cliente</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {needs.map((r, i) => (
                      <tr key={i} data-testid={`mup-need-row-${i}`}>
                        <td className="p-2 text-slate-900">{r.voce}</td>
                        {["richieste", "proposte", "confermate"].map((col) => (
                          <td key={col} className="p-2 text-center">
                            <button type="button" data-testid={`mup-need-${i}-${col}`} onClick={() => toggleNeed(i, col)}
                              className={`h-7 w-7 rounded-full inline-flex items-center justify-center transition-colors ${r[col] ? "bg-emerald-100 text-emerald-600" : "bg-red-100 text-red-500"}`}>
                              {r[col] ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
                            </button>
                          </td>
                        ))}
                        <td className="p-2 text-right">
                          <button type="button" data-testid={`mup-need-remove-${i}`} onClick={() => removeNeed(i)}
                            className="text-slate-400 hover:text-red-500"><X className="h-4 w-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex flex-wrap gap-2 items-center max-w-2xl">
              <Input data-testid="mup-new-voce" value={newVoce} onChange={(e) => setNewVoce(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addVoce())} placeholder="Aggiungi una garanzia/voce…" className="h-9 flex-1 min-w-[220px]" />
              <Button type="button" variant="outline" size="sm" data-testid="mup-add-voce-btn" onClick={addVoce}>
                <Plus className="h-4 w-4 mr-1" /> Aggiungi
              </Button>
              <Button type="button" variant="outline" size="sm" data-testid="mup-bulk-open-btn"
                onClick={() => setShowBulk((s) => !s)} disabled={catalogVoci.length === 0}>
                <ListChecks className="h-4 w-4 mr-1" /> Aggiungi da elenco predefinito
              </Button>
            </div>
            {catalogVoci.length === 0 && (
              <p className="text-xs text-slate-400">Nessuna garanzia predefinita nel Catalogo. Configura le voci in «Catalogo → Aree di tutela» per poterle spuntare qui.</p>
            )}
            {showBulk && (
              <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-3 max-w-2xl" data-testid="mup-bulk-panel">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-slate-700">Spunta le garanzie da inserire</p>
                  <span className="text-xs text-slate-400">{bulkAvailable.length} disponibili</span>
                </div>
                <Input data-testid="mup-bulk-search" value={bulkQuery} onChange={(e) => setBulkQuery(e.target.value)} placeholder="Filtra garanzie…" className="h-8" />
                <div className="max-h-64 overflow-y-auto rounded border border-slate-100 divide-y divide-slate-50">
                  {bulkAvailable.length === 0 ? (
                    <p className="p-3 text-sm text-slate-400">Nessuna garanzia da aggiungere (tutte già presenti o filtro senza risultati).</p>
                  ) : bulkAvailable.map((c) => (
                    <label key={c.voce} data-testid={`mup-bulk-item-${c.voce}`} className="flex items-start gap-2 p-2 cursor-pointer hover:bg-slate-50">
                      <input type="checkbox" className="mt-1 h-4 w-4 accent-brand-blue"
                        checked={!!bulkSel[c.voce]} onChange={(e) => setBulkSel((s) => ({ ...s, [c.voce]: e.target.checked }))} />
                      <span className="text-sm text-slate-700">{c.voce}{c.areaTitolo ? <span className="text-slate-400"> · {c.areaTitolo}</span> : null}</span>
                    </label>
                  ))}
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="ghost" size="sm" onClick={() => { setShowBulk(false); setBulkSel({}); }}>Annulla</Button>
                  <Button type="button" size="sm" data-testid="mup-bulk-add-btn" onClick={addBulkVoci} disabled={bulkCount === 0} className="brand-gradient text-white border-0">
                    <Plus className="h-4 w-4 mr-1" /> Aggiungi selezionate{bulkCount > 0 ? ` (${bulkCount})` : ""}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Prodotto transparency table */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-slate-900">Prodotto</h3>
            <p className="text-xs text-slate-500 -mt-1">Per <b>confrontare più compagnie in un solo MUP</b> (es. Groupama e Assimoco), aggiungi qui una riga per ciascun prodotto/compagnia dal catalogo. Non è necessario creare MUP separati.</p>
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    {PROD_COLS.map((c) => <th key={c.key} className="p-2 font-medium text-left min-w-[110px]">{c.label}</th>)}
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {prodRows.length === 0 ? (
                    <tr><td colSpan={PROD_COLS.length + 1} className="p-3 text-center text-slate-400">Nessun prodotto in tabella. Aggiungine uno dal catalogo qui sotto.</td></tr>
                  ) : prodRows.map((row, i) => (
                    <tr key={i} data-testid={`mup-prod-row-${i}`}>
                      {PROD_COLS.map((c) => (
                        <td key={c.key} className="p-1.5">
                          {c.type === "yn" ? (
                            <YnCell value={row[c.key]} onChange={(v) => setProd(i, c.key, v)} testid={`mup-prod-${i}-${c.key}`} />
                          ) : (
                            <Input value={row[c.key] || ""} onChange={(e) => setProd(i, c.key, e.target.value)}
                              data-testid={`mup-prod-${i}-${c.key}`} className="h-8 text-xs" />
                          )}
                        </td>
                      ))}
                      <td className="p-1.5 text-right">
                        <button type="button" data-testid={`mup-prod-remove-${i}`} onClick={() => removeProd(i)}
                          className="text-slate-400 hover:text-red-500"><X className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Add product from catalog */}
            <div className="flex flex-wrap items-end gap-2">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Aggiungi prodotto dal catalogo</Label>
                <Select value="" onValueChange={addProductRow}>
                  <SelectTrigger data-testid="mup-prod-add-select" className="h-9 w-72"><SelectValue placeholder="Seleziona prodotto…" /></SelectTrigger>
                  <SelectContent>
                    {selectableProducts.map((p) => (
                      <SelectItem key={p.product_id} value={p.product_id}>{companyName(p.company_id) || "—"} · {p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="button" variant="outline" size="sm" data-testid="mup-prod-add-blank" onClick={addBlankRow}>
                <Plus className="h-4 w-4 mr-1" /> Riga vuota
              </Button>
              <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer" data-testid="mup-prod-show-all">
                <input type="checkbox" checked={showAllProducts} onChange={(e) => setShowAllProducts(e.target.checked)} />
                Mostra tutti i prodotti (non solo quelli di quest'area)
              </label>
            </div>

            {/* Quick add company/product to catalog */}
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3">
              <p className="text-xs font-medium text-slate-500">Compagnia o prodotto assente? Aggiungilo al catalogo:</p>
              <div className="flex flex-wrap gap-2">
                <Input data-testid="mup-quick-company" value={newCompany} onChange={(e) => setNewCompany(e.target.value)}
                  placeholder="Nuova compagnia…" className="h-9 w-52" />
                <Button type="button" variant="outline" size="sm" data-testid="mup-quick-company-btn" onClick={quickAddCompany}>
                  <Plus className="h-4 w-4 mr-1" /> Compagnia
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <Input data-testid="mup-quick-product" value={newProdName} onChange={(e) => setNewProdName(e.target.value)}
                  placeholder="Nuovo prodotto…" className="h-9 w-52" />
                <Select value={newProdCompany} onValueChange={setNewProdCompany}>
                  <SelectTrigger data-testid="mup-quick-product-company" className="h-9 w-52"><SelectValue placeholder="Compagnia del prodotto" /></SelectTrigger>
                  <SelectContent>
                    {companies.filter((c) => c.active !== false).map((c) => <SelectItem key={c.company_id} value={c.company_id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" size="sm" data-testid="mup-quick-product-btn" onClick={quickAddProduct}>
                  <Plus className="h-4 w-4 mr-1" /> Prodotto
                </Button>
              </div>
            </div>
          </div>

          {/* Consulenza */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-slate-900">Consulenza</h3>
            <div className="space-y-3">
              {CONSULENZA_ITEMS.map((c) => (
                <label key={c.key} data-testid={`mup-consulenza-${c.key}`} className="flex items-start gap-3 rounded-lg border border-slate-200 p-3 cursor-pointer">
                  <Switch checked={!!cons[c.key]} onCheckedChange={(v) => setCons(c.key, v)} className="mt-0.5" />
                  <span className="text-sm text-slate-700">{c.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Dichiarazione di coerenza */}
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-slate-900">Dichiarazione di coerenza</h3>
            <label className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 cursor-pointer" data-testid="mup-coerenza">
              <Switch checked={!!form.dichiarazione_coerenza} onCheckedChange={(v) => setForm((f) => ({ ...f, dichiarazione_coerenza: v }))} />
              <span className="text-sm text-slate-700 font-medium">Il prodotto è coerente con le richieste ed esigenze assicurative del contraente</span>
            </label>
          </div>
        </>
      )}
    </div>
  );

  function pickArea(areaId) {
    const a = areas.find((x) => x.area_id === areaId);
    setForm((f) => {
      // se cambio area, ricostruisco la griglia dalle voci (mantengo se stessa area)
      const keepGrid = f.area_id === areaId && (f.needs_grid || []).length > 0;
      const grid = keepGrid ? f.needs_grid : (a?.voci || []).map((voce) => ({ voce, richieste: false, proposte: false, confermate: false }));
      return { ...f, area_id: areaId, area_titolo: a?.titolo || "", needs_grid: grid };
    });
  }
}
